import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Box,
  Flex,
  IconButton,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Link2,
  Link2Off,
  List,
  ListOrdered,
} from "lucide-react";
import { useEffect, useCallback } from "react";
import "./RichTextEditor.css";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minH?: string;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = "Digite aqui...",
  minH = "96px",
}: RichTextEditorProps) {
  const toolbarBg  = useColorModeValue("gray.50", "#060d1a");
  const editorBg   = useColorModeValue("gray.50", "#0a1628");
  const borderColor = useColorModeValue("var(--chakra-colors-gray-200)", "rgba(255,255,255,0.08)");
  const btnActiveBg = useColorModeValue("blue.100", "rgba(0,158,251,0.18)");
  const btnActiveColor = useColorModeValue("blue.600", "#60c8ff");
  const btnHoverBg = useColorModeValue("gray.100", "rgba(255,255,255,0.06)");

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.isEmpty ? "" : editor.getHTML();
      onChange(html);
    },
  });

  // Sync external value changes (e.g. when modal resets)
  useEffect(() => {
    if (!editor) return;
    const current = editor.isEmpty ? "" : editor.getHTML();
    if (current !== value) {
      editor.commands.setContent(value || "");
    }
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("URL do link:", prev ?? "https://");
    if (url === null) return; // cancelled
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
  }, [editor]);

  if (!editor) return null;

  const ToolBtn = ({
    label,
    isActive,
    isDisabled,
    onClick,
    children,
  }: {
    label: string;
    isActive?: boolean;
    isDisabled?: boolean;
    onClick: () => void;
    children: React.ReactNode;
  }) => (
    <Tooltip label={label} hasArrow fontSize="xs" placement="top">
      <IconButton
        aria-label={label}
        icon={<>{children}</>}
        size="xs"
        variant="ghost"
        isDisabled={isDisabled}
        onClick={onClick}
        bg={isActive ? btnActiveBg : "transparent"}
        color={isActive ? btnActiveColor : "inherit"}
        _hover={{ bg: isActive ? btnActiveBg : btnHoverBg }}
        _active={{ bg: btnActiveBg }}
        borderRadius="md"
        w="26px"
        h="26px"
        minW="26px"
      />
    </Tooltip>
  );

  return (
    <Box
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      overflow="hidden"
      style={{ "--editor-min-h": minH } as React.CSSProperties}
    >
      {/* Toolbar */}
      <Flex
        align="center"
        gap={0.5}
        px={2}
        py={1}
        bg={toolbarBg}
        borderBottomWidth="1px"
        borderColor={borderColor}
        flexWrap="wrap"
      >
        <ToolBtn
          label="Negrito (Ctrl+B)"
          isActive={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold size={13} />
        </ToolBtn>
        <ToolBtn
          label="Itálico (Ctrl+I)"
          isActive={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic size={13} />
        </ToolBtn>
        <ToolBtn
          label="Sublinhado (Ctrl+U)"
          isActive={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon size={13} />
        </ToolBtn>
        <ToolBtn
          label="Tachado"
          isActive={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough size={13} />
        </ToolBtn>

        <Box w="1px" h="16px" bg={borderColor} mx={1} flexShrink={0} />

        <ToolBtn
          label="Inserir link"
          isActive={editor.isActive("link")}
          onClick={setLink}
        >
          <Link2 size={13} />
        </ToolBtn>
        <ToolBtn
          label="Remover link"
          isDisabled={!editor.isActive("link")}
          onClick={() => editor.chain().focus().unsetLink().run()}
        >
          <Link2Off size={13} />
        </ToolBtn>

        <Box w="1px" h="16px" bg={borderColor} mx={1} flexShrink={0} />

        <ToolBtn
          label="Lista com marcadores"
          isActive={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List size={13} />
        </ToolBtn>
        <ToolBtn
          label="Lista numerada"
          isActive={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered size={13} />
        </ToolBtn>
      </Flex>

      {/* Editor content */}
      <Box bg={editorBg} className="gg-rich-editor" px={3} py={2.5}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}
